import * as R from 'ramda';
import moment from 'moment';

import {
  getTableColumnsForPage,
  maybeDecorateColumn
} from '../../utils/tableColumns';

import { isClientUser } from '../client/ClientReducer';

import {
  FETCH,
  FETCH_ALL,
  DELETE,
  UPDATE,
  FILTER_BY_ACTION,
  TRANSFORM_DATA,
  SHOW_SEARCH_FILTER,
  SELECT_NAVIGATE_FILTER,
  CLEAR_CURRENT_DATA
} from './AttendanceActions';

export const STATE_KEY = 'attendance';

const HIDDEN_FROM_CLIENT = R.path(['WINSTON', 'svrPrivacy', 'HIDDEN_FROM_CLIENT'])(window);

const initialState = {
  data            : {},
  transformedData : {},
  tableColumns    : [],
  filterList      : {}
};

const maybeAppendDecorators = R.map((filter={}) => {
  const decorator = R.path(['WINSTON', 'tableColumns', 'attendance', filter.pathStr, 'decorator'])(window);
  if (R.is(Function, decorator)) {
    return R.assoc('decorator', decorator, filter);
  } else {
    return filter;
  }
});

const appendFilterType = R.map((filter={}) => {
  const type = R.path(['WINSTON', 'tableColumns', 'attendance', filter.pathStr, 'type'])(window);
  return R.assoc('type', type, filter);
});

const ensureClientDoesNotSeeHiddenRecords = _isClientUser => n => {
  if (n.privacy === HIDDEN_FROM_CLIENT) {
    return !_isClientUser;
  } else {
    return true;
  }
};

const isValueBetweenRangeOf = (value, pathStr) => filter => {
  const undecorator = R.path(['WINSTON', 'tableColumns', 'attendance', pathStr, 'undecorator'])(window);
  if (R.is(Function, undecorator)) {
    const range = undecorator(filter);
    return value >= range[0] && value <= range[1];
  }

  return false;
};

const AttendanceReducer = (state = initialState, action) => {
  switch (action.type) {
    case `${FETCH_ALL}_SUCCESS`: {

      const tableColumns = getTableColumnsForPage('attendance', 'attendance');

      return R.compose(
        R.assocPath([R.path(['meta', 'previousAction', 'payload', 'namespace'])(action)], R.__, state),
        R.assoc('tableColumns', tableColumns),
        data => ({ data, transformedData : R.clone(data) }),
        R.indexBy(R.prop('id')),
        R.pathOr([], ['payload', 'data', 'data']),
      )(action);
    }

    case `${FETCH}_SUCCESS`: {
      const data      = R.pathOr({}, ['payload', 'data', 'data'])(action),
            namespace = R.path(['meta', 'previousAction', 'payload', 'namespace'])(action);
      const _isClientUser = isClientUser();

      if (ensureClientDoesNotSeeHiddenRecords(_isClientUser)(data)) {
        return R.compose(
          R.assocPath([namespace], R.__, state),
          R.assoc('transformedData', [R.clone(data)]),
          R.assocPath(['data', data.id+''], data),
        )({});
      } else {
        return R.assocPath([namespace, 'data', data.id+''], {}, state);
      }
    }

    case `${UPDATE}_SUCCESS`: {
      const modifySvrResponse = R.pathOr({}, ['payload', 'data', 'data'], action);
      const namespace         = R.path(['meta', 'previousAction', 'payload', 'namespace'])(action);
      return R.ifElse(
        R.isEmpty,
        R.always(state),
        () => R.assocPath([namespace, 'data', modifySvrResponse.id+''], modifySvrResponse, state)
      )(modifySvrResponse);
    }

    case `${DELETE}_SUCCESS`: {
      const storeVisitId = R.path(['meta', 'previousAction', 'payload', 'storeVisitId'])(action);
      return R.compose(
        R.dissocPath(['data', `${storeVisitId}`]),
        R.over(R.lensProp('transformedData'), R.reject(R.propEq('id', storeVisitId))),
      )(state);
    }

    case FILTER_BY_ACTION : {

      const filterList = R.compose(
        maybeAppendDecorators,
        appendFilterType
      )(action.payload.filters);

      const filterListNeedsUpdating = R.compose(
        R.not,
        R.equals(filterList),
        R.pathOr({}, [action.payload.namespace, 'filterList'])
      )(state);

      if (filterListNeedsUpdating) {

        const _state = R.assocPath([action.payload.namespace, 'filterList'], filterList, state);

        if (!R.isEmpty(filterList)) {
          try {
            window.localStorage.setItem(`persistedFilters:${action.payload.namespace}`, JSON.stringify(filterList));
          } catch (e) {
            return _state;
          }
        } else {
          try {
            window.localStorage.removeItem(`persistedFilters:${action.payload.namespace}`);
          } catch (e) {
            return _state;
          }
        }

        return _state;
      } else {
        return state;
      }

    }

    case TRANSFORM_DATA : {

      const emptyData = R.cond([
        [R.equals('string'), R.always('')],
        [R.equals('number'), R.always(0)],
        [R.equals('date'),   R.equals(new Date(-8640000000000000))],
        [R.T,                R.always('')]
      ])(action.payload.sortType);

      const _isClientUser = isClientUser();

      const compareValueIfFilterHasSelections = props => {
        const path        = props[0],
              data        = props[1],
              decorator   = props[2],
              type        = props[3],
              pathStr     = props[4],
              parsedData  = type === 'number' ? R.map(d => parseInt(d, 10))(data) : data;

        const filterableValue = v => {
          if (type === 'range') {
            return R.path(path, v);
          }
          return maybeDecorateColumn(decorator)(R.path(path, v));
        };

        const isWithinDateRange = R.anyPass(
          R.map(range => date => moment(date, 'MM/DD/YYYY hh:mm:A').isBetween(moment(range[0], 'YYYY-MM-DD'), moment(range[1], 'YYYY-MM-DD'), 'day', '[]'))(data)
        );

        const isWithinRange = value => R.anyPass(
          R.map(isValueBetweenRangeOf(value, pathStr), data)
        );

        const finder = R.cond([
          [R.equals('date'), () => isWithinDateRange],
          [R.equals('range'), () => isWithinRange],
          [R.T, () => R.either(R.contains(R.__, parsedData), R.contains(R.__, data))]
        ])(type);

        return parsedData.length ? R.compose(finder, filterableValue) : R.T;
      };

      const maybeStringToNumber  = value => {
        if (action.payload.sortType === 'number' && R.is(String, value)) {
          return parseInt(value, 10);
        }

        return value;
      };
      const maybeLowerCaseString = R.when(R.is(String), R.toLower);
      const ensureEmptyTypeValue = R.unless(R.identity, R.always(emptyData));

      const allFilters = R.compose(
        R.map(compareValueIfFilterHasSelections),
        R.values,
        R.map(R.props(['path', 'data', 'decorator', 'type', 'pathStr']))
      )(action.payload.filterList);

      const transformedData = R.compose(
        R.sort(
          R[`${action.payload.order}end`](
            R.compose(
              maybeStringToNumber,
              ensureEmptyTypeValue,
              maybeLowerCaseString,
              R.path(action.payload.orderBy.split('.'))
            )
          )
        ),
        R.filter(R.both(
          R.allPass(allFilters),
          ensureClientDoesNotSeeHiddenRecords(_isClientUser)
        ))
      )(action.payload.data);

      return R.assocPath([action.payload.namespace, 'transformedData'], transformedData)(state);
    }

    case SHOW_SEARCH_FILTER : {
      return { ...state, showSearchFilter : action.payload };
    }

    case SELECT_NAVIGATE_FILTER : {
      return { ...state, navigateFilter: action.payload };
    }

    case CLEAR_CURRENT_DATA : {
      return R.assocPath([action.payload.namespace, 'data' ], [], state);
    }

    default: {
      return state;
    }
  }
};

export const getAllStoreVisits = (state, namespace=STATE_KEY) => R.values(
  R.pathOr([], [STATE_KEY, namespace, 'data'], state)
) || [];

export const getTransformedData = (state, namespace=STATE_KEY) => R.values(
  R.pathOr([], [STATE_KEY, namespace, 'transformedData'], state)
) || [];

export const getStoreVisit      = (state, namespace, id) => {
  return R.pathOr({}, [STATE_KEY, namespace, 'data', id], state);
};

const getInitialFilters = () => {
  const tableColumn = R.find(R.propEq('pathStr', 'visitDurationStatus'), getTableColumnsForPage('attendance', 'attendance'));
  return {
    [tableColumn.label] : {
      pathStr   : tableColumn.pathStr,
      path      : tableColumn.path,
      label     : tableColumn.label,
      decorator : tableColumn.decorator,
      data      : ['Unapproved']
    }
  };
};

const rangeValues = {
  'visit_duration_variance': [
    { label: '+ 02:01 and greater' },
    { label: '+ 01:31 to 02:00' }, 
    { label: '+ 01:01 to 01:30' },
    { label: '+ 00:31 to 01:00' },
    { label: '+ 00:11 to 00:30' },
    { label: '+ 00:01 to 00:10' },
    { label: '- 00:01 to 00:10' },
    { label: '- 00:11 to 00:30' },
    { label: '- 00:31 to 01:00' },
    { label: '- 01:01 to 01:30' },
    { label: '- 01:31 to 02:00' }, 
    { label: '- 02:01 and greater' }
  ]
};
const prefilledFilters = () => ({
  "Variance": rangeValues['visit_duration_variance'].map((_, idx) => ({
    label: rangeValues['visit_duration_variance'][idx].label
  }))
});

export const getTableColumns     = namespace => R.pathOr(getTableColumnsForPage('attendance', 'attendance'),    [STATE_KEY, namespace, 'tableColumns']);
export const getFilterList       = namespace => R.pathOr(getInitialFilters(),    [STATE_KEY, namespace, 'filterList']);
export const getShowSearchFilter = R.pathOr(false, [STATE_KEY, 'showSearchFilter']);
export const getNavigateFilter   = R.pathOr(null,  [STATE_KEY, 'navigateFilter']);

export const getPrefilledFilters = prefilledFilters;

export default AttendanceReducer;
